import { paintGameBoard } from './35-game-board.mjs';
import { createGemEffects } from './41-gem-effects.mjs';
import { gemMoves } from '../../../packages/domain/src/60-gem-powers.mjs';
import { uiText } from '../04-i18n.mjs';
import { jewelArtwork, ORCHARD_CHARM_ATLAS, ORCHARD_BURST_ATLAS } from './40-jewel-artwork.mjs';

/** A finite, skippable visual guide warms original art without gating native
 * fallback play. Only a versioned tab-local dismissal is remembered; no account,
 * score, seed or player behaviour is saved. Loading failures leave Skip usable. */
export function createGemGuide(ctx, { playfield, board, auto = false }) {
  if (typeof window.Image !== 'function') return null;
  const panel = ctx.e('section', 'game-gem-guide');
  panel.setAttribute('role', 'region');
  panel.setAttribute('aria-label', uiText('How to play'));
  panel.hidden = true;
  const caption = ctx.e('strong', 'game-gem-guide-caption');
  caption.setAttribute('aria-live', 'polite');
  const art = ctx.e('div', 'game-gem-guide-art');
  art.setAttribute('aria-hidden', 'true');
  const steps = ctx.e('span', 'game-gem-guide-steps');
  const loading = ctx.e('span', 'game-gem-guide-loading');
  loading.setAttribute('role', 'status');
  const skip = ctx.e('button', 'game-action');
  const next = ctx.e('button', 'game-action');
  const actions = ctx.e('div', 'game-gem-guide-actions');
  actions.append(steps, loading, skip, next);
  panel.append(caption, art, actions);
  playfield.append(panel);
  let step = 0,
    disposed = false,
    requested = false,
    remaining = 3;
  const images = [],
    key = 'myaudience-gem-guide-v1';
  const translate = ctx.uiText || uiText;
  function action(node, icon, label) {
    node.type = 'button';
    node.setAttribute('aria-label', translate(label));
    node.title = translate(label);
    node.dataset.iconOnly = String(Boolean(ctx.icon));
    node.replaceChildren(ctx.icon ? ctx.icon(icon) : ctx.e('span', '', translate(label)));
  }
  action(skip, 'x', 'Skip introduction');
  function prepare() {
    if (requested) return;
    requested = true;
    loading.textContent = '…';
    loading.setAttribute('aria-label', translate('Preparing artwork'));
    for (const url of [
      new URL('../assets/game-orchard-daylight-v1.webp', import.meta.url),
      new URL(ORCHARD_CHARM_ATLAS),
      new URL(ORCHARD_BURST_ATLAS),
    ]) {
      const image = new window.Image();
      images.push(image);
      let complete = false;
      const finish = () => {
        if (disposed || complete) return;
        complete = true;
        if (--remaining === 0) {
          loading.textContent = '✓';
          loading.setAttribute('aria-label', translate('Ready to play'));
        }
      };
      image.onload = image.onerror = finish;
      image.src = url.href;
      image.decode?.().then(finish, () => {});
    }
  }
  function paint() {
    const count = step + 3;
    caption.textContent = translate(
      ['Swap neighbouring fruit to match 3', 'Match 4 for a Bloom', 'Match 5 for a Harvest'][step],
    );
    art.replaceChildren();
    art.dataset.step = String(step);
    const colours = step === 0 ? [0, 1, 0, 2, 0, 3] : Array(count).fill(step);
    for (const colour of colours) {
      const jewel = ctx.e('span', 'game-gem-guide-jewel');
      jewel.append(jewelArtwork(ctx, { colour, value: colour }, '◆'));
      art.append(jewel);
    }
    if (step) {
      const result = ctx.e('span', 'game-gem-guide-result');
      result.append(
        ctx.e('span', '', '→'),
        jewelArtwork(
          ctx,
          { colour: step, value: step, special: step === 1 ? 'nova' : 'spectrum' },
          '◆',
        ),
      );
      art.append(result);
    }
    steps.textContent = `${step + 1} / 3`;
    action(next, step === 2 ? 'play' : 'arrow-right', step === 2 ? 'Play now' : 'Next step');
  }
  function cancel() {
    panel.hidden = true;
    board.inert = false;
  }
  function close() {
    cancel();
    try {
      window.sessionStorage.setItem(key, 'seen');
    } catch {
      /* Tab memory is optional. */
    }
    board.children[0]?.focus?.({ preventScroll: true });
  }
  function show(focus = true) {
    if (disposed) return;
    step = 0;
    prepare();
    paint();
    panel.hidden = false;
    board.inert = true;
    if (focus) skip.focus?.({ preventScroll: true });
  }
  skip.addEventListener('click', close);
  next.addEventListener('click', () => {
    if (step === 2) close();
    else {
      step++;
      paint();
    }
  });
  panel.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  });
  let seen = false;
  try {
    seen = window.sessionStorage.getItem(key) === 'seen';
  } catch {
    /* Usable without storage. */
  }
  if (auto && !seen) show(false);
  return {
    show,
    cancel,
    dispose() {
      disposed = true;
      cancel();
      for (const image of images) {
        image.onload = image.onerror = null;
        image.removeAttribute('src');
      }
      panel.remove();
    },
  };
}

/** Offer a native, named hint action without adding moves to ranked transcripts. */
export function createGemHint(action, playback, feedback, readState, enabled) {
  return action(
    '',
    'Show a possible fruit swap',
    () => {
      if (!enabled()) return;
      const pair = playback.hint(readState());
      if (!pair) return;
      const position = (index) => `row ${Math.floor(index / 8) + 1}, column ${(index % 8) + 1}`;
      feedback.textContent = `${uiText('Swap the highlighted fruit')} · ${pair.map(position).join(' ↔ ')}`;
    },
    'lightbulb',
  );
}

/** Replay advisory gem transition snapshots without changing the accepted game.
 * All eight canonical cascades animate; invalid excess settles to the final state.
 * Cancellation, hidden pages, reduced motion and missing animation APIs settle
 * immediately. No animation supplies points, moves, timers or permissions. */
export function createGemPlayback(ctx, board, playfield, { settle, onClear } = {}) {
  let generation = 0;
  let active = null;
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  function preferenceChanged() {
    if (!media.matches) return;
    const accepted = active;
    cancel();
    if (accepted && board.isConnected)
      paintGameBoard(ctx, board, accepted, () => {}, { disabled: true, motion: false });
    if (accepted) settle?.(accepted);
  }
  media.addEventListener?.('change', preferenceChanged);
  let animations = [];
  let effects = [];
  let pending = false;
  const effectCleanup = new Map();
  function releaseEffect(effect) {
    effectCleanup.get(effect)?.();
    effectCleanup.delete(effect);
    effect.remove();
  }
  function cancel() {
    generation++;
    delete board.gameDragGem;
    for (const animation of animations) animation.cancel();
    for (const effect of effects) releaseEffect(effect);
    animations = [];
    effects = [];
    pending = false;
    active = null;
  }
  function paint(previous, values, specials) {
    const snapshot = structuredClone(previous);
    snapshot.completed = false;
    snapshot.data.board = [...values];
    snapshot.data.specials = [...specials];
    snapshot.data.selected = -1;
    paintGameBoard(ctx, board, snapshot, () => {}, { disabled: true, motion: false });
  }
  function animate(node, frames, duration) {
    if (typeof node?.animate !== 'function') return;
    const animation = node.animate(frames, { duration, fill: 'forwards', easing: 'ease-out' });
    animations.push(animation);
    return animation;
  }
  async function wait(retain = []) {
    const batch = animations,
      nodes = effects.filter((node) => !retain.includes(node));
    await Promise.all(batch.map((animation) => animation.finished.catch(() => {})));
    for (const animation of batch) animation.cancel();
    for (const effect of nodes) releaseEffect(effect);
    if (animations === batch) animations = [];
    effects = effects.filter((node) => !nodes.includes(node));
  }
  const visualEffects = createGemEffects(
    ctx,
    board,
    playfield,
    (node, cleanup) => {
      effects.push(node);
      if (cleanup) effectCleanup.set(node, cleanup);
    },
    animate,
  );
  function play(previous, finalState, events) {
    const drag = board.gameDragGem;
    delete board.gameDragGem;
    const draggedSwap =
      drag &&
      drag.seed === previous.seed &&
      drag.previousMoves === previous.moves &&
      finalState.moves === previous.moves + 1 &&
      previous.data.selected === drag.source &&
      events.some(
        (event) =>
          event.type === 'swap' &&
          event.cells[0] === drag.source &&
          event.cells[1] === drag.destination,
      );
    cancel();
    if (
      finalState.gameId !== 'gem-swap' ||
      !events.some((event) => ['clear', 'shuffle', 'invalid'].includes(event.type)) ||
      document.hidden ||
      media.matches ||
      typeof board.children[0]?.animate !== 'function'
    )
      return null;
    const token = generation;
    pending = true;
    active = finalState;
    const current = () =>
      token === generation && board.isConnected && !document.hidden && !media.matches;
    return (async () => {
      let clears = 0;
      for (const event of events) {
        if (!current()) break;
        if (event.type === 'shuffle' && event.boardAfter) {
          const prior = events.slice(0, events.indexOf(event)).findLast((item) => item.boardAfter);
          paint(
            previous,
            prior?.boardAfter || previous.data.board,
            prior?.specialsAfter || previous.data.specials,
          );
          onClear?.(event);
          const toast = ctx.e('div', 'game-gem-refill');
          toast.setAttribute('aria-hidden', 'true');
          toast.append(
            ctx.e(
              'strong',
              '',
              uiText(event.reason.startsWith('no swaps') ? 'NO MOVES LEFT!' : 'FRESH FRUIT!'),
            ),
            ctx.e('span', '', uiText('Refilling · your powers are safe')),
          );
          playfield.append(toast);
          effects.push(toast);
          // Keep this global notice through both finite phases, while individual
          // jewels remain owned by the same cancellation generation.
          for (const cell of board.children)
            animate(
              cell,
              [
                { opacity: 1, transform: 'scale(1)' },
                { opacity: 0, transform: 'translateY(12px) scale(.55)' },
              ],
              180,
            );
          try {
            await wait([toast]);
            if (!current()) break;
            paint(previous, event.boardAfter, event.specialsAfter);
            const columns = Math.sqrt(event.boardAfter.length);
            for (let i = 0; i < board.children.length; i++)
              animate(
                board.children[i],
                [
                  { opacity: 0, transform: 'translateY(-90px) scale(.65)' },
                  { opacity: 1, transform: 'translateY(2px) scale(1.04)', offset: 0.78 },
                  { opacity: 1, transform: 'translateY(0) scale(1)' },
                ],
                340 + Math.floor(i / columns) * 14 + (i % columns) * 9,
              );
            await wait();
          } finally {
            releaseEffect(toast);
            effects = effects.filter((node) => node !== toast);
          }
        } else if (event.type === 'invalid' && event.boardBefore) {
          paint(previous, event.boardBefore, event.specialsBefore);
          onClear?.(event);
          for (const index of event.cells)
            animate(
              board.children[index],
              [
                { transform: 'translateX(0)' },
                { transform: 'translateX(-5px)', offset: 0.25 },
                { transform: 'translateX(4px)', offset: 0.6 },
                { transform: 'translateX(0)' },
              ],
              220,
            );
          await wait();
        } else if (event.type === 'swap' && event.boardBefore) {
          const [a, b] = event.cells;
          const values = [...event.boardBefore],
            specials = [...event.specialsBefore];
          if (draggedSwap) {
            [values[a], values[b]] = [values[b], values[a]];
            [specials[a], specials[b]] = [specials[b], specials[a]];
          }
          paint(previous, values, specials);
          const first = board.children[a],
            second = board.children[b];
          const rect = first.getBoundingClientRect(),
            other = second.getBoundingClientRect();
          const x = other.left - rect.left,
            y = other.top - rect.top;
          if (!draggedSwap)
            animate(
              first,
              [{ transform: 'translate(0,0)' }, { transform: `translate(${x}px,${y}px)` }],
              160,
            );
          animate(
            draggedSwap ? first : second,
            draggedSwap
              ? [{ transform: `translate(${x}px,${y}px)` }, { transform: 'translate(0,0)' }]
              : [{ transform: 'translate(0,0)' }, { transform: `translate(${-x}px,${-y}px)` }],
            160,
          );
          await wait();
        } else if (event.type === 'clear' && event.boardBefore) {
          if (++clears > 8) break;
          paint(previous, event.boardBefore, event.specialsBefore);
          onClear?.(event);
          visualEffects.clear(event);
          for (const index of event.cells)
            animate(
              board.children[index],
              [
                { opacity: 1, transform: 'scale(1)' },
                { opacity: 1, transform: 'scale(1.12)', offset: 0.3 },
                { opacity: 0, transform: 'scale(.2)' },
              ],
              240,
            );
          await wait();
        } else if (event.type === 'earned') {
          paint(previous, event.boardAfter, event.specialsAfter);
          for (const [index] of event.pieces || [])
            animate(
              board.children[index],
              [
                { filter: 'brightness(1)', transform: 'scale(1)' },
                { filter: 'brightness(2)', transform: 'scale(1.18)', offset: 0.4 },
                { filter: 'brightness(1)', transform: 'scale(1)' },
              ],
              300,
            );
          await wait();
        } else if (event.type === 'gravity' && event.boardAfter) {
          paint(previous, event.boardAfter, event.specialsAfter);
          const firstRect = board.children[0].getBoundingClientRect();
          const columns = Math.sqrt(event.boardAfter.length);
          const height =
            board.children[columns]?.getBoundingClientRect().top - firstRect.top ||
            firstRect.height;
          for (const [from, to] of event.movements || [])
            animate(
              board.children[to],
              [
                {
                  transform: `translateY(${(Math.floor(from / columns) - Math.floor(to / columns)) * height}px)`,
                },
                { transform: 'translateY(0)' },
              ],
              Math.min(
                360,
                210 + Math.abs(Math.floor(from / columns) - Math.floor(to / columns)) * 18,
              ),
            );
          for (const [index] of event.refills || []) {
            const count = event.refills.filter(
              ([target]) => target % columns === index % columns,
            ).length;
            animate(
              board.children[index],
              [
                { opacity: 0, transform: `translateY(${-height * count}px)` },
                { opacity: 1, transform: 'translateY(0)' },
              ],
              Math.min(420, 260 + count * 18),
            );
          }
          await wait();
        }
      }
    })()
      .catch(() => {})
      .finally(() => {
        if (token === generation) {
          cancel();
          settle?.();
        }
      });
  }
  return {
    play,
    cancel,
    hint(state) {
      if (pending || state.completed || document.hidden || !board.isConnected) return null;
      cancel();
      const pair = gemMoves(state.data.board, state.data.specials)[0];
      if (!pair) return null;
      for (const index of pair) {
        const cell = board.children[index];
        cell.dataset.hint = 'true';
        // Cells belong to the native board; only the hint decoration is disposable.
        effects.push({
          remove() {
            delete cell.dataset.hint;
          },
        });
        if (!media.matches)
          animate(
            cell,
            [
              { transform: 'scale(1)' },
              { transform: 'scale(1.1)', offset: 0.35 },
              { transform: 'scale(1)', offset: 0.7 },
              { transform: 'scale(1.06)' },
            ],
            650,
          );
      }
      return pair;
    },
    dispose() {
      cancel();
      media.removeEventListener?.('change', preferenceChanged);
    },
    get pending() {
      return pending;
    },
  };
}
